import React, { Fragment, useEffect, useMemo } from "react";
import { FormTypeName } from "../formType/FormTypeName";
import { Header, Message, Button, Segment } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useApplicationConfiguration } from "../../configuration/ConfigurationContext";
import SchemaForm from "../SchemaForm";
import { isSubmitErrored, isSubmitted, isSubmitting, isLoaded, isLoadErrored } from "../../../../sg-core";
import { TFunction } from "i18next";
import { useLoggedInUser } from "../../auth/store/authSelectors";
import { useHistory } from "react-router-dom";
import { useNewObservation } from "../../store/selectors/observationSelectors";
import { useFormDefinitionById, useFormTypeById } from "../../store/selectors/formSelectors";
import {
    createInitializeNewObservatioFormAction,
    createNewObservationFormDataUpdateAction,
    createRequestFetchCreateObservationAction,
} from "../../store/actions/observationActions";
import { useDispatch } from "react-redux";
import { useUserCanViewNames } from "../../store/selectors/appRoleSelector";

const getTextProps = (t: TFunction) => ({
    headerText: t("Tee uusi havainto"),
    saveButtonText: t("Tallenna"),
    cancelButtonText: t("Peruuta"),

    submitSuccessText: t("Tallennus onnistui"),
    submitSuccessLongText: t("Havainto tallennettiin onnistuneesti ja odottaa käsittelijää."),
    submitErrorText: t("Tallennuksessa tapahtui virhe!"),
    formLoadErrorText: t("Lomakkeen haku epäonnistui!"),
});

interface IObservationForm {
    formTypeGuid: string | number;
    userRequired?: boolean;
    canCancel?: boolean;
    onSuccess: () => void;

    initialFormData?: any;
    onChange?: (values: any) => void;
}

const ObservationForm: React.FC<IObservationForm> = ({
    formTypeGuid,
    userRequired = true,
    canCancel = true,
    onSuccess,

    initialFormData,
    onChange,
}) => {
    const dispatch = useDispatch();

    const history = useHistory();
    const goBack = () => history.goBack();

    const user = useLoggedInUser();

    const { t } = useTranslation();
    const texts = getTextProps(t);

    const newObservation = useNewObservation();
    const formType = useFormTypeById(typeof formTypeGuid === "string" ? (formTypeGuid as any) : undefined);
    const formDefinition = useFormDefinitionById(formType?.formDefinition);
    const canViewNames = useUserCanViewNames(formType?.category);

    const autofillInformer = useMemo(() => {
        if (isLoaded(formDefinition)) {
            const skipInformerAutofillForRoles =
                formDefinition.data.schema.havainnot?.skipInformerAutofillForRoles;
            const skipAutofillForRoles =
                skipInformerAutofillForRoles && Array.isArray(skipInformerAutofillForRoles)
                    ? skipInformerAutofillForRoles
                    : skipInformerAutofillForRoles
                    ? [skipInformerAutofillForRoles]
                    : [];
            const hasOneOfPreventingRoles = user?.roles.find((role) =>
                skipAutofillForRoles.includes(role.reference),
            );
            return !hasOneOfPreventingRoles;
        }
        return true;
    }, [formDefinition, user]);

    const informer = autofillInformer ? user?.fullname || t("Anonyymi") : undefined;

    const formData = initialFormData || {
        ...(newObservation ? newObservation.data.formData : {}),
        ilmottaja: informer,
    };

    const { observationPublicByDefault } = useApplicationConfiguration();

    // Initialize form
    useEffect(() => {
        if (formType && (userRequired === false || user)) {
            dispatch(createInitializeNewObservatioFormAction(formType, user, observationPublicByDefault));
        }
    }, [dispatch, formType, userRequired, user, observationPublicByDefault]);

    const handleSubmit = (values: { [key: string]: any }) => {
        dispatch(createNewObservationFormDataUpdateAction(values));
        dispatch(createRequestFetchCreateObservationAction());
    };

    // Render

    const formSubmitted = newObservation && isSubmitted(newObservation);
    useEffect(() => {
        if (formSubmitted) {
            onSuccess();
        }
    }, [formSubmitted]);

    if (formSubmitted) {
        return <Message content={t("Lomake tallennettu")} negative />;
    }
    if (isLoadErrored(formDefinition)) {
        return <Message content={texts.formLoadErrorText} negative />;
    }

    if (formType && !formType?.formDefinition) {
        return <Message content={t("Lomakkeen määritystiedosto puuttuu")} negative />;
    }

    if (!isLoaded(formDefinition)) {
        return <Segment loading placeholder />;
    }

    // const informer = autofillInformer ? user?.fullname || t("Anonyymi") : undefined;
    // const formData = { ...(newObservation ? newObservation.data.formData : {}), ilmottaja: informer };

    return (
        <Fragment>
            <Header size="large" as="h1">
                {texts.headerText}{" "}
            </Header>
            <div>{formType?.id && <FormTypeName formTypeId={formType.id} />}</div>
            <br />

            {newObservation && isSubmitErrored(newObservation) ? (
                <Message negative>{texts.submitErrorText}</Message>
            ) : null}
            {isLoaded(formDefinition) ? (
                <SchemaForm
                    formSchema={formDefinition.data.schema}
                    onSubmit={handleSubmit}
                    onChange={onChange}
                    formData={formData}
                    canViewNames={canViewNames}
                >
                    {newObservation && isSubmitErrored(newObservation) ? (
                        <Message negative>{texts.submitErrorText}</Message>
                    ) : null}
                    <br />
                    <Button primary type="submit" loading={newObservation && isSubmitting(newObservation)}>
                        {texts.saveButtonText}
                    </Button>
                    {canCancel && (
                        <Button type="button" onClick={goBack}>
                            {" "}
                            {texts.cancelButtonText}
                        </Button>
                    )}
                </SchemaForm>
            ) : isLoadErrored(formDefinition) ? (
                <Message content={texts.formLoadErrorText} negative />
            ) : (
                <Segment placeholder loading basic />
            )}

            {!isLoaded(formDefinition) ? (
                <>
                    <Button primary disabled>
                        {texts.saveButtonText}
                    </Button>
                    {canCancel && <Button onClick={goBack}> {texts.cancelButtonText}</Button>}
                </>
            ) : null}
        </Fragment>
    );
};

export default ObservationForm;
